import React, { useContext } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { getAiRolePlay } from 'redux/selectors/timeline';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';

import { Button } from 'react-bootstrap';
import NvIcon from 'shared/components/nv-icon';
import NvGradientBorderButton from 'shared/components/button/nv-gradient-border-button';
import { doubleSpacing } from 'styles/global_defaults/scaffolding';
import GradientAiIcon from '../../left-panel/novo-ai/components/shared/gradient-ai-icon';
import AiConversationContext, { AIRolePlayStatus } from './context';
import useAiRolePlayHelper from '../hooks/use-ai-role-play-helper';

const styles = css`
  min-height: 70px;
  .practice-again-button {
    height: ${doubleSpacing}px;
  }
`;

type ConversationModalHeaderProps = {
  startConversation: () => void;
  isRolePlayStarting: boolean;
};

const ConversationModalHeader = (props: ConversationModalHeaderProps) => {
  const { startConversation, isRolePlayStarting } = props;
  const dispatch = useDispatch();
  const {
    aiRolePlayStatus,
    aiRolePlayId,
    handleExitChat,
  } = useContext(AiConversationContext);

  const { isMobile } = useAiRolePlayHelper();
  const aiRolePlay = useSelector((state) => getAiRolePlay(state, aiRolePlayId));
  const { title } = aiRolePlay || {};

  const handleStartOver = () => {
    dispatch(openConfirmationDialog({
      title: t.FORM.ARE_YOU_SURE(),
      bodyText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER_CONFIRMATION.MESSAGE(),
      confirmText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER_CONFIRMATION.CONFIRM(),
      cancelText: t.FORM.CANCEL(),
      onConfirm: () => {
        startConversation();
      },
    }));
  };

  const handlePracticeAgain = () => {
    startConversation();
  };

  return (
    <div css={styles} className={`border-bottom d-flex ${isMobile ? 'px-4' : 'px-6'}`}>
      <GradientAiIcon icon='gen-ai' size='smallest' className='pr-2 pt-4' />
      <div className={`d-flex pt-4 ${isMobile ? 'flex-column' : ''}`}>
        <p className={`mb-0 text-regular bold ${!isMobile ? 'mr-1' : ''}`}>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.ROLE_PLAY()}
        </p>
        <p className='mb-0'>{title}</p>
      </div>
      {aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS && (
        <div className='ml-auto d-flex pt-3 pb-4'>
          <Button
            className='bg-transparent shadow-none border-0 text-gray-1 d-flex px-0'
            onClick={handleStartOver}
            disabled={isRolePlayStarting}
            aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER()}
          >
            <NvIcon
              icon='refresh'
              size='small'
              className='d-flex align-items-center'
            />
            {!isMobile && (
              <p className='mb-0 ml-2'>
                {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER()}
              </p>
            )}
          </Button>
          <Button
            variant='danger'
            className={isMobile ? 'ml-4' : 'ml-5'}
            onClick={() => handleExitChat()}
            aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT()}
          >
            {isMobile
              ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT()
              : t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT()}
          </Button>
        </div>
      )}
      {aiRolePlayStatus === AIRolePlayStatus.COMPLETED && (
        <div className='ml-auto d-flex pt-3 pb-4'>
          <NvGradientBorderButton
            onClick={handlePracticeAgain}
            className='align-items-center py-2 px-5 practice-again-button'
            ariaLabel={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PRACTICE_AGAIN()}
            disabled={isRolePlayStarting}
          >
            {isRolePlayStarting
              ? <NvIcon icon='gen-ai' size='small' className='pr-2 text-gray-4' />
              : <GradientAiIcon icon='gen-ai' size='small' className='pr-2' />}
            <span className={`${isRolePlayStarting ? 'text-gray-4' : ''} text-regular bold`}>
              {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PRACTICE_AGAIN()}
            </span>
          </NvGradientBorderButton>
        </div>
      )}
    </div>
  );
};

export default ConversationModalHeader;
